import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaWhatsapp, FaEnvelope, FaPhone, FaCheckCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import productCatalog from '../data/productCatalog.json';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [category, setCategory] = useState('');

  useEffect(() => {
    // Find product in all categories
    let foundProduct = null;
    let foundCategory = '';

    Object.keys(productCatalog).forEach((cat) => {
      const item = productCatalog[cat].find(p => p.id === id);
      if (item) {
        foundProduct = item;
        foundCategory = cat;
      }
    });

    if (foundProduct) {
      setProduct(foundProduct);
      setCategory(foundCategory);
    }

    // Scroll to top
    window.scrollTo(0, 0);
  }, [id]);

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="product-not-found">
            <h2>Product Not Found</h2>
            <p>The product you're looking for doesn't exist.</p>
            <Link to="/products" className="btn btn-primary">Back to Products</Link>
          </div>
        </div>
      </div>
    );
  }

  // Generate multiple product images (for carousel demo)
  const productImages = [
    product.image,
    product.image,
    product.image,
    product.image
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const handleWhatsAppInquiry = () => {
    const message = `Hi, I'm interested in ${product.brand} ${product.model}. Can you provide more details?`;
    const phone = '+919876543210'; // Replace with your WhatsApp number
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getCategoryName = () => {
    const categoryMap = {
      'speakers': 'Speakers',
      'amplifiers': 'Amplifiers',
      'microphones': 'Microphones',
      'mixers': 'DSP\'s',
      'accessories': 'Cables & Connectors'
    };
    return categoryMap[category] || category;
  };

  return (
    <div className="product-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/products">Products</Link>
          <span>/</span>
          <span>{getCategoryName()}</span>
          <span>/</span>
          <span>{product.brand} {product.model}</span>
        </div>
      </div>

      <div className="product-detail-container">
        <div className="container">
          <button className="back-button" onClick={() => navigate('/products')}>
            <FaArrowLeft /> Back to Products
          </button>

          <div className="product-detail-grid">
            {/* Left Side - Image Carousel */}
            <div className="product-images-section">
              <div className="main-image-container">
                <img 
                  src={`https://images.unsplash.com/photo-1545454660-877a4a8c4a9e?w=600&h=600&fit=crop&q=80`}
                  alt={`${product.brand} ${product.model}`}
                  className="main-product-image"
                />
                <button className="image-nav-btn prev-btn" onClick={prevImage}>
                  <FaChevronLeft />
                </button>
                <button className="image-nav-btn next-btn" onClick={nextImage}>
                  <FaChevronRight />
                </button>
                <div className="image-counter">
                  {currentImageIndex + 1} / {productImages.length}
                </div>
              </div>
              
              <div className="thumbnail-container">
                {productImages.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img 
                      src={`https://images.unsplash.com/photo-1545454660-877a4a8c4a9e?w=100&h=100&fit=crop&q=80`}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Product Info */}
            <div className="product-info-section">
              <div className="product-header">
                <span className="product-category-badge">{getCategoryName()}</span>
                <h1 className="product-title">
                  {product.brand} {product.model}
                </h1>
                {product.type && <p className="product-type">{product.type}</p>}
              </div>

              <div className="product-price-section">
                <div className="price-tag">
                  <span className="price">₹{product.price.toLocaleString('en-IN')}</span>
                  <span className="price-note">+ GST</span>
                </div>
                <div className="price-info">
                  <FaCheckCircle /> Authorized Dealer
                </div>
              </div>

              {/* Key Features */}
              <div className="product-highlights">
                <h3>Key Features</h3>
                <ul className="highlights-list">
                  {category === 'speakers' && (
                    <>
                      <li><FaCheckCircle /> <strong>Size:</strong> {product.size}</li>
                      <li><FaCheckCircle /> <strong>Power:</strong> {product.powerRating}W</li>
                      <li><FaCheckCircle /> <strong>Frequency Range:</strong> {product.frequencyRange}</li>
                      <li><FaCheckCircle /> <strong>Coverage:</strong> {product.coverageArea} sq.ft</li>
                      {product.indoor && <li><FaCheckCircle /> Indoor Use</li>}
                      {product.outdoor && <li><FaCheckCircle /> Outdoor Use</li>}
                    </>
                  )}
                  {category === 'amplifiers' && (
                    <>
                      <li><FaCheckCircle /> <strong>Power Output:</strong> {product.powerOutput}W per channel</li>
                      <li><FaCheckCircle /> <strong>Channels:</strong> {product.channels}</li>
                      <li><FaCheckCircle /> <strong>Cooling:</strong> {product.cooling}</li>
                      <li><FaCheckCircle /> Professional Grade Protection</li>
                    </>
                  )}
                  {category === 'microphones' && (
                    <>
                      <li><FaCheckCircle /> <strong>Type:</strong> {product.type}</li>
                      <li><FaCheckCircle /> <strong>Pattern:</strong> {product.pattern}</li>
                      <li><FaCheckCircle /> <strong>Frequency:</strong> {product.frequencyResponse}</li>
                      <li><FaCheckCircle /> Professional {product.connector} Connector</li>
                    </>
                  )}
                  {category === 'mixers' && (
                    <>
                      <li><FaCheckCircle /> <strong>Channels:</strong> {product.channels}</li>
                      <li><FaCheckCircle /> <strong>Type:</strong> {product.type}</li>
                      <li><FaCheckCircle /> <strong>Aux Sends:</strong> {product.auxSends}</li>
                      {product.effects && <li><FaCheckCircle /> Built-in Effects</li>}
                      {product.usb && <li><FaCheckCircle /> USB Recording Interface</li>}
                    </>
                  )}
                </ul>
              </div>

              {/* CTA Buttons */}
              <div className="product-actions">
                <button className="btn-primary btn-large" onClick={handleWhatsAppInquiry}>
                  <FaWhatsapp /> Get Quote on WhatsApp
                </button>
                <Link to="/contact" className="btn-secondary btn-large">
                  <FaEnvelope /> Contact for Details
                </Link>
                <a href="tel:+919876543210" className="btn-outline btn-large">
                  <FaPhone /> Call Now
                </a>
              </div>

              {/* Trust Signals */}
              <div className="trust-signals">
                <div className="trust-item">
                  <FaCheckCircle />
                  <span>7+ Years Experience</span>
                </div>
                <div className="trust-item">
                  <FaCheckCircle />
                  <span>Professional Installation</span>
                </div>
                <div className="trust-item">
                  <FaCheckCircle />
                  <span>Warranty Included</span>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications Table */}
          <div className="product-specifications">
            <h2>Technical Specifications</h2>
            <div className="spec-table">
              <table>
                <tbody>
                  <tr>
                    <td className="spec-label">Brand</td>
                    <td className="spec-value">{product.brand}</td>
                  </tr>
                  <tr>
                    <td className="spec-label">Model</td>
                    <td className="spec-value">{product.model}</td>
                  </tr>
                  <tr>
                    <td className="spec-label">Category</td>
                    <td className="spec-value">{getCategoryName()}</td>
                  </tr>
                  {product.type && (
                    <tr>
                      <td className="spec-label">Type</td>
                      <td className="spec-value">{product.type}</td>
                    </tr>
                  )}
                  
                  {/* Speaker-specific specs */}
                  {category === 'speakers' && (
                    <>
                      <tr>
                        <td className="spec-label">Speaker Size</td>
                        <td className="spec-value">{product.size}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Power Rating</td>
                        <td className="spec-value">{product.powerRating}W</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Sensitivity</td>
                        <td className="spec-value">{product.sensitivity} dB</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Max SPL</td>
                        <td className="spec-value">{product.maxSPL} dB</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Frequency Range</td>
                        <td className="spec-value">{product.frequencyRange}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Coverage Area</td>
                        <td className="spec-value">{product.coverageArea} sq.ft</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Coverage Angle</td>
                        <td className="spec-value">{product.coverageAngle.horizontal}° H x {product.coverageAngle.vertical}° V</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Indoor/Outdoor</td>
                        <td className="spec-value">
                          {product.indoor && 'Indoor'} {product.indoor && product.outdoor && '/'} {product.outdoor && 'Outdoor'}
                        </td>
                      </tr>
                    </>
                  )}

                  {/* Amplifier-specific specs */}
                  {category === 'amplifiers' && (
                    <>
                      <tr>
                        <td className="spec-label">Power Output</td>
                        <td className="spec-value">{product.powerOutput}W per channel</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Channels</td>
                        <td className="spec-value">{product.channels}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Impedance</td>
                        <td className="spec-value">{product.impedance.join('Ω, ')}Ω</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Cooling System</td>
                        <td className="spec-value">{product.cooling}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Protection</td>
                        <td className="spec-value">{product.protection.join(', ')}</td>
                      </tr>
                    </>
                  )}

                  {/* Microphone-specific specs */}
                  {category === 'microphones' && (
                    <>
                      <tr>
                        <td className="spec-label">Polar Pattern</td>
                        <td className="spec-value">{product.pattern}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Frequency Response</td>
                        <td className="spec-value">{product.frequencyResponse}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Connector</td>
                        <td className="spec-value">{product.connector}</td>
                      </tr>
                    </>
                  )}

                  {/* Mixer-specific specs */}
                  {category === 'mixers' && (
                    <>
                      <tr>
                        <td className="spec-label">Total Channels</td>
                        <td className="spec-value">{product.channels}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">XLR Inputs</td>
                        <td className="spec-value">{product.inputs.xlr}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Aux Sends</td>
                        <td className="spec-value">{product.auxSends}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">Built-in Effects</td>
                        <td className="spec-value">{product.effects ? 'Yes' : 'No'}</td>
                      </tr>
                      <tr>
                        <td className="spec-label">USB Interface</td>
                        <td className="spec-value">{product.usb ? 'Yes' : 'No'}</td>
                      </tr>
                    </>
                  )}

                  <tr>
                    <td className="spec-label">Applications</td>
                    <td className="spec-value">{product.applications?.map(app => app.replace('_', ' ')).join(', ')}</td>
                  </tr>
                  <tr>
                    <td className="spec-label">Price</td>
                    <td className="spec-value"><strong>₹{product.price.toLocaleString('en-IN')} + GST</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
